import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { ProcessedAssetHistory } from '../../helpers/processAssetFullHistory';
import { sumBy } from 'lodash';
import {
  numberToCurrencyString,
  numberToPercentageString,
  numberToDisplayString,
} from 'further-ui/utils';
import { AssetHistoryType } from 'further-types/lp/asset';
import { Column } from 'further-ui/components/Layout';
import Typography from '@mui/material/Typography';

type AssetHistoryTableProps = {
  assetHistory: Array<ProcessedAssetHistory>;
  showSummary?: boolean;
  showHoldingClassName?: boolean;
};

const AssetHistoryTypeLabels: Record<AssetHistoryType, string> = {
  [AssetHistoryType.Investment]: 'Investment',
  [AssetHistoryType.Revaluation]: 'Revaluation',
  [AssetHistoryType.Realisation]: 'Realisation',
};

const AssetHistoryTable: React.FC<AssetHistoryTableProps> = ({
  assetHistory,
  showSummary = true,
  showHoldingClassName = true,
}) => {
  const moreThanOneCurrency =
    assetHistory.length > 1 &&
    assetHistory.some(
      (entry) =>
        entry.currency !== undefined &&
        assetHistory[0].currency !== undefined &&
        entry.currency !== assetHistory[0].currency,
    );

  const formattedRows = assetHistory.map((entry) => ({
    ...entry,
    unrealisedValue: numberToCurrencyString(entry.unrealisedValue, {
      currency: entry.currency,
    }),
    unitPrice: numberToCurrencyString(entry.unitPrice, {
      currency: entry.currency,
      unlimitedDp: true,
    }),
    sharesHeld: numberToDisplayString(entry.sharesHeld),
    cumulativeSharesSold: numberToDisplayString(entry.cumulativeSharesSold),
    cumulativeRealisedValue: numberToCurrencyString(
      entry.cumulativeRealisedValue,
      {
        currency: entry.currency,
      },
    ),
    valuationChange:
      entry.valuationChange !== undefined
        ? numberToCurrencyString(entry.valuationChange, {
            currency: entry.currency,
          })
        : '-',
    valuationChangePercentage:
      entry.valuationChangePercentage !== undefined
        ? numberToPercentageString(entry.valuationChangePercentage)
        : '-',
  }));

  const unrealisedValueTotal = sumBy(assetHistory, 'unrealisedValue');
  const valuationChangeTotal = sumBy(assetHistory, 'valuationChange');
  const cumulativeRealisedValueTotal =
    assetHistory.length > 0
      ? assetHistory[assetHistory.length - 1].cumulativeRealisedValue
      : 0;

  const entriesWithChange = assetHistory.filter(
    (entry) => entry.valuationChange !== undefined,
  );
  const firstEntryWithChange = entriesWithChange[0];
  const lastEntry = assetHistory[assetHistory.length - 1];
  const totalPercentageChange =
    firstEntryWithChange && lastEntry
      ? (lastEntry.unrealisedValue -
          (firstEntryWithChange.unrealisedValue -
            (firstEntryWithChange.valuationChange ?? 0))) /
        (firstEntryWithChange.unrealisedValue -
          (firstEntryWithChange.valuationChange ?? 0))
      : 0;

  const footerCurrency =
    formattedRows.length > 0 ? assetHistory[0].currency : undefined;
  const showFooter =
    showSummary && !moreThanOneCurrency && formattedRows.length > 0;

  const columns: Array<TableColumn<(typeof formattedRows)[number]>> = [
    ...(showHoldingClassName
      ? [
          {
            label: 'Holding class',
            key: 'holdingClassName',
            disableSorting: true,
            footer: showFooter ? 'Totals' : undefined,
          },
        ]
      : []),
    {
      label: 'Valuation date',
      key: 'date',
      disableSorting: true,
      render: (row) => row.date.toReadable(),
    },
    {
      label: 'Function',
      key: 'type',
      disableSorting: true,
      render: (row) => AssetHistoryTypeLabels[row.type],
    },
    {
      label: 'Unrealised value',
      key: 'unrealisedValue',
      disableSorting: true,
      footer: showFooter
        ? numberToCurrencyString(unrealisedValueTotal, {
            currency: footerCurrency,
          })
        : undefined,
    },
    {
      label: 'Unit price',
      key: 'unitPrice',
      disableSorting: true,
    },
    {
      label: 'Units held',
      key: 'sharesHeld',
      disableSorting: true,
    },
    {
      label: 'Valuation change',
      key: 'valuationChange',
      disableSorting: true,
      render: ({ valuationChange, valuationChangePercentage }) => (
        <Column>
          <Typography variant="inherit">{valuationChange}</Typography>
          {valuationChange !== '-' ? (
            <Typography variant="body2">{valuationChangePercentage}</Typography>
          ) : null}
        </Column>
      ),
      footer: showFooter ? (
        <Column>
          <Typography variant="inherit">
            {numberToCurrencyString(valuationChangeTotal, {
              currency: footerCurrency,
            })}
          </Typography>
          <Typography variant="body2">
            {numberToPercentageString(totalPercentageChange, {
              forceDecimals: true,
              accuracy: 2,
            })}
          </Typography>
        </Column>
      ) : undefined,
    },
    {
      label: 'Units sold',
      key: 'cumulativeSharesSold',
      disableSorting: true,
    },
    {
      label: 'Realised value',
      key: 'cumulativeRealisedValue',
      disableSorting: true,
      footer: showFooter
        ? numberToCurrencyString(cumulativeRealisedValueTotal, {
            currency: footerCurrency,
          })
        : undefined,
    },
  ];

  return <Table borderless columns={columns} rows={formattedRows} />;
};

export default AssetHistoryTable;
